import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

import Navbar from './components/layout/Navbar';
import Sidebar from './components/layout/Sidebar';
import Footer from './components/layout/Footer';
import LoadingSpinner from './components/ui/LoadingSpinner';
import './App.css';

// Chunk load error recovery
const handleChunkError = (error: Error | undefined): boolean => {
  if (!error) return false;

  const isChunkError = error.name === 'ChunkLoadError' ||
    /Loading chunk .* failed/.test(error.message) ||
    /Failed to fetch dynamically imported module/.test(error.message);

  if (!isChunkError) return false;

  const hasReloaded = sessionStorage.getItem('chunk_error_reload');

  if (!hasReloaded) {
    sessionStorage.setItem('chunk_error_reload', '1');
    window.location.reload();
    return true;
  }

  // If already reloaded once, clear cache and try again
  sessionStorage.removeItem('chunk_error_reload');

  const hasCaches = 'caches' in window && typeof caches !== 'undefined';

  if (hasCaches) {
    caches.keys().then((keys: string[]) => {
      return Promise.all(keys.map((key: string) => caches.delete(key)));
    }).then(() => {
      window.location.reload();
    }).catch(() => {
      window.location.reload();
    });
  } else {
    window.location.reload();
  }

  return true;
};

// Lazy load all page components with error recovery
const lazyWithRetry = (importFunc: () => Promise<any>) => {
  return lazy(() =>
    importFunc().catch((error) => {
      if (handleChunkError(error)) {
        // Return a promise that never resolves to prevent further rendering
        return new Promise(() => { });
      }
      throw error;
    })
  );
};

const Home = lazyWithRetry(() => import('./pages/Home'));
const Store = lazyWithRetry(() => import('./pages/Store'));
const AssetPreview = lazyWithRetry(() => import('./pages/AssetPreview'));
const AdminPanel = lazyWithRetry(() => import('./pages/AdminPanel'));
const ThumbnailEditor = lazyWithRetry(() => import('./components/editor/ThumbnailEditor'));
const PenCropEditor = lazyWithRetry(() => import('./components/editor/PenCropEditor'));


// SEO and legal pages - loaded from separate component files
const PrivacyPolicy = lazyWithRetry(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazyWithRetry(() => import('./pages/TermsOfService'));

// About component is now imported from separate file
const AboutImport = lazyWithRetry(() => import('./pages/About'));

// Contact component is now imported from separate file
const ContactImport = lazyWithRetry(() => import('./pages/Contact'));

// Blog component is now imported from separate file
const BlogImport = lazyWithRetry(() => import('./pages/Blog'));

// BlogPost component for individual blog posts
const BlogPostImport = lazyWithRetry(() => import('./pages/BlogPost'));

// FAQ component for frequently asked questions
const FAQImport = lazyWithRetry(() => import('./pages/FAQ'));

// No authentication required - all routes are public

// Main App Layout Component
const AppLayout: React.FC = () => {
  console.log('=== APP LAYOUT DEBUG ===');
  console.log('AppLayout - Component rendered');

  const location = useLocation();
  const isEditorPage = location.pathname === '/editor';

  console.log('AppLayout - Current location:', location.pathname);
  console.log('AppLayout - Is editor page:', isEditorPage);

  return (
    <div className="min-h-[100dvh] bg-black text-white">
      {/* Hide navbar on small screens for editor to maximize space */}
      <div className={isEditorPage ? 'hidden md:block' : ''}>
        <Navbar />
      </div>
      <div className="flex min-h-[100dvh]">
        {/* Main content */}
        <main className={`flex-1 min-h-[100dvh] ${isEditorPage ? 'pt-0 md:pt-16' : 'pt-16 lg:pr-80'}`}>
          <Suspense
            fallback={
              <div className="flex items-center justify-center min-h-[100dvh]">
                <LoadingSpinner />
              </div>
            }
          >
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/store" element={<Store />} />
              <Route path="/asset/:id" element={<AssetPreview />} />
              <Route path="/admin" element={<AdminPanel />} />
              <Route path="/editor" element={<ThumbnailEditor />} />
              <Route path="/pen-crop" element={<PenCropEditor />} />


              {/* SEO and AdSense compliance pages */}
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/about" element={<AboutImport />} />
              <Route path="/contact" element={<ContactImport />} />
              <Route path="/blog" element={<BlogImport />} />
              <Route path="/blog/:id" element={<BlogPostImport />} />
              <Route path="/faq" element={<FAQImport />} />

              {/* Catch all route */}
              <Route path="*" element={<Home />} />
            </Routes>
          </Suspense>
        </main>
        {/* Sidebar - Hidden only on editor page */}
        {!isEditorPage && <Sidebar />}
      </div>
      {/* Footer - Hidden only on editor page */}
      {!isEditorPage && <Footer />}
      {/* <FloatingCTA /> */}
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: '#1f2937',
            color: '#fff',
            border: '1px solid #374151',
          },
        }}
      />
    </div>
  );
};

function App() {
  console.log('App - Component rendered');

  // Global error handler for unhandled chunk load errors
  useEffect(() => {
    const errorHandler = (event: ErrorEvent) => {
      if (handleChunkError(event.error)) {
        event.preventDefault();
      }
    };

    window.addEventListener('error', errorHandler);
    return () => window.removeEventListener('error', errorHandler);
  }, []);

  // Monetag push notification and interstitial ad scripts - TEMPORARILY DISABLED FOR ADSENSE APPROVAL
  // These scripts were causing the page to show a blank screen on production
  // Re-enable after AdSense approval if needed
  /*
  useEffect(() => {
    const pushScript = document.createElement('script');
    pushScript.dataset.zone = '10146150';
    pushScript.src = 'https://forfrogadiertor.com/tag.min.js';
    pushScript.async = true;
    document.body.appendChild(pushScript);

    // Monetag interstitial ad script
    const interstitialScript = document.createElement('script');
    interstitialScript.dataset.zone = '10146214';
    interstitialScript.src = 'https://groleegni.net/vignette.min.js';
    interstitialScript.async = true;
    document.body.appendChild(interstitialScript);

    return () => {
      // Cleanup: remove scripts when app unmounts
      if (pushScript.parentNode) {
        pushScript.parentNode.removeChild(pushScript);
      }
      if (interstitialScript.parentNode) {
        interstitialScript.parentNode.removeChild(interstitialScript);
      }
    };
  }, []);
  */


  return (
    <Router>
      <AppLayout />
    </Router>
  );
}

export default App;
